"use client";

import { Check, Copy, Info, Lightbulb, MessageSquare } from "lucide-react";
import { useState } from "react";

import { ChatGPTIcon, ClaudeIcon } from "@/components/admin/brand-icons";
import { useEnvPreview } from "@/lib/use-env-preview";

/* ══════════════════════════════════════════════════════════════
   MCP integration guide — design system v2 ("Dune").

   Layout from `design_handoff_alister_app/app-static.html` (the MCP guide
   screen). The handoff mocks four steps; the real flow has six — the
   "Always allow" and "Verify" steps are kept and continue the design's
   color rhythm (blue → amber → red → ink → amber → blue).
══════════════════════════════════════════════════════════════ */

// Both MCP endpoints are hardcoded so the admin "preview prod" toggle
// can swap the displayed URL without a rebuild. The build-time
// NEXT_PUBLIC_ALISTER_MCP_URL override still wins when set, so local
// dev on a nonstandard host keeps working.
const DEV_MCP_URL = "https://dev.alisterai.com/mcp";
const PROD_MCP_URL = "https://alisterai.com/mcp";
const BUILD_MCP_URL = process.env.NEXT_PUBLIC_ALISTER_MCP_URL;

interface Props {
  /** Start collapsed (default) or open. */
  defaultOpen?: boolean;
  /** Title — override per context (admin vs user). */
  title?: string;
  /**
   * Reserved for future use. The API-key tab is currently hidden everywhere
   * (the prop is accepted so existing call sites don't have to be updated).
   */
  showApiKeyTab?: boolean;
}

// Claude.ai web + ChatGPT (web) only — both verified to work via OAuth + DCR.
// Claude mobile widget rendering and Claude Desktop OAuth aren't reliable yet.
// Flip SHOW_CHATGPT_TAB back on once the ChatGPT path is verified end-to-end.
const SHOW_CHATGPT_TAB = false;

type ClientKind = "claude" | "chatgpt";

const CLIENT_COPY: Record<
  ClientKind,
  {
    subtitle: string;
    step1Title: React.ReactNode;
    step1Desc: React.ReactNode;
    step3Title: React.ReactNode;
    step3Desc: React.ReactNode;
  }
> = {
  claude: {
    subtitle: "Claude.ai web",
    step1Title: (
      <>
        Add a new custom connector named{" "}
        <code className="al-inline-code">Alister</code>
      </>
    ),
    step1Desc: (
      <>
        In Claude.ai: <em>Settings → Connectors → Add custom connector</em>.
      </>
    ),
    step3Title: (
      <>
        Leave OAuth Client ID / Secret fields <strong>blank</strong>
      </>
    ),
    step3Desc: (
      <>
        Alister uses Dynamic Client Registration — your client self-registers
        automatically. No API key needed.
      </>
    ),
  },
  chatgpt: {
    subtitle: "ChatGPT web (Plus / Pro / Team / Enterprise)",
    step1Title: (
      <>
        Enable Developer Mode and create a new app named{" "}
        <code className="al-inline-code">Alister</code>
      </>
    ),
    step1Desc: (
      <>
        In ChatGPT: <em>Settings → Apps → Advanced settings</em>, toggle{" "}
        <strong>Developer Mode</strong> on. A <strong>Create app</strong>{" "}
        button appears — click it.
      </>
    ),
    step3Title: (
      <>
        Confirm <strong>Authentication: OAuth</strong> and accept the security
        warning
      </>
    ),
    step3Desc: (
      <>
        Set <em>Authentication</em> to <strong>OAuth</strong> (the default).
        OAuth settings are auto-discovered from the URL via Dynamic Client
        Registration. Tick the &ldquo;<em>I understand and want to continue</em>
        &rdquo; checkbox, then click <strong>Create</strong>.
      </>
    ),
  },
};

export function McpConnectionInstructions({ defaultOpen = true, title }: Props) {
  // `defaultOpen` and `title` kept for API compatibility, but the design
  // is always-open inline content (no collapse), with a fixed hero header.
  void defaultOpen;
  void title;

  const [client, setClient] = useState<ClientKind>("claude");
  const [copied, setCopied] = useState(false);
  const envPreview = useEnvPreview();
  // Admin "preview prod" swaps the displayed URL between envs. When an
  // explicit build-time override is set, honor it on the env it matches.
  const mcpUrl = envPreview === "prod" ? PROD_MCP_URL : BUILD_MCP_URL ?? DEV_MCP_URL;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore — clipboard may be blocked */
    }
  };

  const copy = CLIENT_COPY[client];
  const headerTitle =
    client === "claude" ? "Connect Alister to Claude.ai" : "Connect Alister to ChatGPT";

  return (
    <>
      {/* the logo's node pair, drifting behind the headline */}
      <svg className="al-mcp-orn" viewBox="0 0 444 434" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g fill="#DDD8CE">
          <circle cx="122" cy="120" r="85" />
          <circle cx="318" cy="318" r="92" />
          <path d="M61.6,179.8 C158.6,214.1 224.6,280.1 252.6,382.7 L383.4,253.3 C281.4,223.9 215.4,157.9 182.4,60.2 Z" />
        </g>
      </svg>

      <span className="al-kicker al-kicker-outline">
        <span className="al-dots">
          <i style={{ background: "var(--al-red)" }} />
          <i style={{ background: "var(--al-amber)" }} />
          <i style={{ background: "var(--al-blue)" }} />
        </span>
        MCP integration guide
      </span>

      <h1>
        Use Alister from <em>inside</em>{" "}
        {SHOW_CHATGPT_TAB ? "Claude or ChatGPT" : "Claude.ai"}
      </h1>

      <p className="al-mcp-lead">
        Connect Alister to{" "}
        <strong>{SHOW_CHATGPT_TAB ? "your AI client" : "Claude.ai web"}</strong> in 5 minutes.
        Once linked,{" "}
        <strong>search for developers, pull profiles, and build shortlists</strong> — all
        through natural language, without leaving your AI assistant.
      </p>

      {/* Client selector — a single ink CTA while Claude is the only verified path */}
      {SHOW_CHATGPT_TAB ? (
        <div className="al-mcp-foot">
          <button
            type="button"
            className={`al-btn${client === "claude" ? "" : " al-btn-ghost"}`}
            onClick={() => setClient("claude")}
          >
            <ClaudeIcon size={18} />
            Claude.ai
          </button>
          <button
            type="button"
            className={`al-btn${client === "chatgpt" ? "" : " al-btn-ghost"}`}
            onClick={() => setClient("chatgpt")}
          >
            <ChatGPTIcon size={18} />
            ChatGPT
          </button>
        </div>
      ) : (
        <a className="al-btn-claude" href="https://claude.ai" target="_blank" rel="noopener noreferrer">
          <i />
          Claude.ai
        </a>
      )}

      <div className="al-guide">
        <div className="rhythm">
          <i style={{ background: "var(--al-blue)" }} />
          <i style={{ background: "var(--al-amber)" }} />
          <i style={{ background: "var(--al-red)" }} />
        </div>

        <div className="body">
          <div className="ghead">
            <span className="gmark">
              <ClaudeIcon size={22} />
            </span>
            <span>
              <span className="gt">{headerTitle}</span>
              <span className="gs">{copy.subtitle}</span>
            </span>
          </div>

          {/* Step 1 — add connector / create app */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-blue)" }}>1</span>
            <div>
              <div className="al-stept">{copy.step1Title}</div>
              <div className="al-stepb">{copy.step1Desc}</div>
            </div>
          </div>

          {/* Step 2 — paste URL */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-amber)", color: "var(--al-ink)" }}>2</span>
            <div>
              <div className="al-stept">Paste this MCP URL and save</div>
              <div className="al-code">
                <input
                  value={mcpUrl}
                  readOnly
                  onClick={(e) => e.currentTarget.select()}
                  aria-label="MCP server URL"
                />
                <button
                  type="button"
                  className={copied ? "copied" : undefined}
                  onClick={handleCopy}
                  aria-label={copied ? "Copied" : "Copy URL"}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>

          {/* Step 3 — auth (per-client copy) */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-red)" }}>3</span>
            <div>
              <div className="al-stept">{copy.step3Title}</div>
              <div className="al-stepb">{copy.step3Desc}</div>
            </div>
          </div>

          {/* Step 4 — sign in + tools */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-ink)" }}>4</span>
            <div>
              <div className="al-stept">Sign in with your Alister credentials</div>
              <div className="al-stepb">
                Your client will open an Alister sign-in window. Once authenticated, these
                tools become available:
              </div>
              <div className="al-tools">
                <span className="al-tool-b">search_profiles</span>
                <span className="al-tool-b">view_profiles</span>
                <span className="al-tool-r">export_file</span>
                <span className="al-tool-b">browse_projects</span>
              </div>
            </div>
          </div>

          {/* Step 5 — always allow */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-amber)", color: "var(--al-ink)" }}>5</span>
            <div>
              <div className="al-stept">
                Set all tools to <strong>Always allow</strong>
              </div>
              <div className="al-callout al-callout-tip">
                <Lightbulb size={16} />
                <div>
                  Open the connector&apos;s <strong>Configure</strong> panel and flip every
                  tool to <strong>Always allow</strong> — otherwise your client will ask
                  permission before every single tool call mid-chat.
                </div>
              </div>
            </div>
          </div>

          {/* Step 6 — verify */}
          <div className="al-mcp-step">
            <span className="al-stepn" style={{ background: "var(--al-blue)" }}>6</span>
            <div>
              <div className="al-stept">Verify your connection is working</div>
              <div className="al-verify">
                <MessageSquare size={16} />
                <span>
                  <span className="lbl">Try asking</span>
                  <span className="prompt">
                    &ldquo;Find me a senior Python developer experienced in FastAPI&rdquo;
                  </span>
                </span>
              </div>
              <div className="al-callout al-callout-info">
                <Info size={14} />
                <div>
                  <strong>First-time sign-in</strong> auto-creates an Alister account if
                  your email isn&apos;t already registered. Rate limits are shared across
                  all connected clients.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
